import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  imageAlt,
  imageWidth = '1200',
  imageHeight = '630',
  url,
  type = 'website',
  author = 'BlueLagoon & Partners',
  siteName = 'BlueLagoon & Partners',
  locale = 'en_US',
  twitterHandle = '@BLPPartners',
  structuredData,
  noindex = false,
  nofollow = false,
}) => {
  const defaultTitle = 'BlueLagoon & Partners | Property Management & Investment';
  const defaultDescription = 'Professional crowdfunded property management and investment opportunities. Invest in premium real estate with expert guidance and transparent returns.';
  const defaultKeywords = 'real estate investment, property crowdfunding, real estate management, property investment, BlueLagoon, property opportunities, Dubai investment';
  const defaultImage = 'https://theblpartners.com/images/BlueLagoon-_-Partners-png.png';
  const baseUrl = 'https://theblpartners.com';

  const seoTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;
  // Default to the unbranded title only — never include the "| BlueLagoon &
  // Partners" suffix in image alt text. If imageAlt isn't passed, falling back
  // to the bare title is a reasonable description of the image.
  const seoImageAlt = imageAlt || title || 'BlueLagoon & Partners';
  const seoUrl = url ? `${baseUrl}${url}` : baseUrl;

  // Determine robots meta content
  const robotsContent = noindex || nofollow
    ? `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />

      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />

      {/* hreflang intentionally omitted: i18next translates client-side but
          the site does not yet have separate locale URLs. Emitting hreflang
          tags that all point to the same English URL is treated by Google as
          misconfigured and can demote international rankings. Re-enable when
          dedicated locale routes (e.g. /es/, /fr/) exist. */}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:alt" content={seoImageAlt} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={seoImageAlt} />

      {/* Additional SEO Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#0077b6" />
      <meta name="msapplication-TileColor" content="#0077b6" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;